/**
 * Reads `/auth/config`, decides which sign-in path applies, and publishes a
 * `Session` for the rest of the app.
 *
 * Under `entra` with a configured registration, an MSAL client is built from
 * the config and the tree is wrapped in `MsalProvider`; `EntraSession` inside
 * it translates MSAL's account list and interaction state into the `Session`
 * shape. Under `mock`, or under `entra` with no client id, no MSAL instance is
 * ever created and the session says so.
 *
 * Redirect flow, not popup (phase 4 doc; the inventory app's own sign-in
 * notes): more reliable across browsers, and the redirect target is the app's
 * own origin, which the SPA fallback already serves.
 */
import type { IPublicClientApplication } from "@azure/msal-browser";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { fetchAuthConfig, type AuthConfigOut } from "../api/public";
import { canInitialiseMsal, createMsalClient } from "./msalClient";
import { SessionContext, type Session } from "./session";

type Boot =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; config: AuthConfigOut; client: IPublicClientApplication | null };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<Boot>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await fetchAuthConfig();
        const client = canInitialiseMsal(config) ? await createMsalClient(config) : null;
        if (!cancelled) setBoot({ kind: "ready", config, client });
      } catch (error) {
        if (!cancelled) {
          setBoot({ kind: "error", message: error instanceof Error ? error.message : String(error) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (boot.kind === "loading") {
    return <SessionContext.Provider value={{ kind: "loading" }}>{children}</SessionContext.Provider>;
  }
  if (boot.kind === "error") {
    return (
      <SessionContext.Provider value={{ kind: "error", message: boot.message }}>
        {children}
      </SessionContext.Provider>
    );
  }

  const { config, client } = boot;
  if (client === null) {
    const session: Session =
      config.auth_mode === "mock" ? { kind: "mock", config } : { kind: "unconfigured", config };
    return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
  }

  return (
    <MsalProvider instance={client}>
      <EntraSession config={config}>{children}</EntraSession>
    </MsalProvider>
  );
}

function EntraSession({ config, children }: { config: AuthConfigOut; children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  const scopes = config.scopes;

  const session = useMemo<Session>(() => {
    if (inProgress !== InteractionStatus.None) {
      return { kind: "signing-in", config };
    }
    if (!account) {
      return {
        kind: "signed-out",
        config,
        signIn: () => instance.loginRedirect({ scopes }),
      };
    }
    return {
      kind: "signed-in",
      config,
      upn: account.username,
      getAccessToken: async () => {
        try {
          const result = await instance.acquireTokenSilent({ scopes, account });
          return result.accessToken;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            // Leaves the page; the promise never resolves. The redirect lands
            // back on this origin with a fresh token in the cache.
            await instance.acquireTokenRedirect({ scopes, account });
          }
          throw error;
        }
      },
      signOut: () => instance.logoutRedirect({ account }),
    };
  }, [account, config, inProgress, instance, scopes]);

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
